import { useCallback, useEffect, useState } from "react";

import PropTypes from "prop-types";

import { Button, Dialog, DialogActions, DialogContent } from "@mui/material";
import {
  DataGrid,
  gridRowSelectionIdsSelector,
  useGridApiRef,
} from "@mui/x-data-grid";

import { useOutletApi } from "@/features/authentication";
import { formatNumber } from "@/features/shared";
import { CustomDialogTitle } from "@/features/ui";

/**
 * Picks expense lines (SIE) off the sales invoice a return is sourced from.
 * A return may only ever charge back expenses that exist on that invoice, so
 * this list is the complete set of choices — there is no free-form entry here.
 */
export const SelectSalesInvoiceExpenseDialog = ({
  outletId,
  referenceNumber,
  attachedExpenses,
  onClose,
  onSubmit,
}) => {
  const apiRef = useGridApiRef();
  const outletApi = useOutletApi(outletId);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedExpenses, setSelectedExpenses] = useState([]);

  const fetchExpenses = useCallback(async () => {
    if (!referenceNumber) return;

    setLoading(true);

    try {
      const {
        data: { data },
      } = await outletApi.get(
        `sales-invoices/${referenceNumber}/sales-invoice-expenses`
      );

      // Remaining returnable is the source line's amount minus what earlier
      // returns already took, minus what this return has attached so far.
      const returnableExpenses = data
        .map((expense) => {
          const alreadyAttached = attachedExpenses
            .filter((attached) => attached.salesInvoiceExpenseId === expense.id)
            .reduce((total, attached) => total + (attached.amount || 0), 0);

          return {
            ...expense,
            remainingAmount:
              expense.amount - (expense.returnedAmount || 0) - alreadyAttached,
          };
        })
        .filter((expense) => expense.remainingAmount > 0);

      setExpenses(returnableExpenses);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
    // attachedExpenses is intentionally read once per open: re-running on every
    // edit would refetch the list while the user is choosing from it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outletApi, referenceNumber]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const columns = [
    {
      field: "outletChartOfAccount",
      headerName: "Akun",
      flex: 3,
      valueGetter: (value) => (value ? `${value.code} - ${value.name}` : "-"),
    },
    {
      field: "note",
      headerName: "Catatan",
      flex: 2,
      valueGetter: (value) => value || "-",
    },
    {
      field: "amount",
      headerName: "Nominal",
      type: "number",
      flex: 1,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "remainingAmount",
      headerName: "Sisa Retur",
      type: "number",
      flex: 1,
      valueFormatter: (value) => formatNumber(value),
    },
  ];

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <CustomDialogTitle
        variant="h5"
        title="Ambil Biaya Faktur"
        onClose={onClose}
      />
      <DialogContent dividers>
        <DataGrid
          getRowHeight={() => "auto"}
          apiRef={apiRef}
          columns={columns}
          rows={expenses}
          loading={loading}
          checkboxSelection
          onRowSelectionModelChange={() => {
            const selectedModels = Array.from(
              gridRowSelectionIdsSelector(apiRef.current.state).values()
            );
            setSelectedExpenses(selectedModels);
          }}
          disableRowSelectionOnClick
          hideFooter
          sx={{ minHeight: 300 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Batal</Button>
        <Button
          variant="contained"
          disabled={selectedExpenses.length === 0}
          onClick={() => {
            onSubmit(selectedExpenses);
            onClose();
          }}
        >
          Tambah
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SelectSalesInvoiceExpenseDialog.propTypes = {
  outletId: PropTypes.string,
  referenceNumber: PropTypes.string,
  attachedExpenses: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};
